<template>
  <div class="calculation-steps result">
    <h2 class="h4">
      Calculation Steps:
    </h2>
    <ol type="I">
      <li>
        <p class="roman-number">
          Calculate the Mean
        </p>
        <ol>
          <li>
            <div>
              <p>Add up all the values in the data set.</p>
              <p class="formulas">
                \[\sum_{i=1}^{n} X_i = {{ additionString }}\]
              </p>
            </div>
          </li>
          <li>
            <div class="top-margins">
              <p>Count the number of observations:</p>
              <p class="formulas">
                \[n = {{ getLengthOfArray }}\]
              </p>
            </div>
          </li>
          <li>
            <div class="top-margins">
              <p>Divide the sum by the number of observations:</p>
              <p class="formulas">
                \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ additionString }} }{ {{ getLengthOfArray }} }={{ calculationData.mean }}\]
              </p>
            </div>
          </li>
        </ol>
      </li>
      <li>
        <p class="roman-number top-margins">
          Calculate the Median.
        </p>
        <ol>
          <li>
            <p>Sort the values into ascending order.</p>
            <p class="formulas">
              \[{{ sortedStringifiedArray() }}\]
            </p>
          </li>
          <li>
            <p class="top-margins">
              Determine the number of observations (n).
            </p>
            <p>\[n = {{ getLengthOfArray }}\]</p>
          </li>
          <li>
            <div v-if="evenOrOdd" class="median-third top-margins">
              <p>
                Since the sample size is <strong>even</strong> and has <strong>{{ getLengthOfArray }}</strong> observations, the median is the average of the values that occupy the n/2 = {{ getLengthOfArray }}/2 = <strong>{{ calculationData.firstPosition }}th</strong> and (n + 2)/2= ({{ getLengthOfArray }}+2)/2=<strong>{{ calculationData.secondPosition }}th</strong> positions. The <strong>{{ calculationData.firstPosition }}th</strong> number is <strong>{{ theTwoNumbers()[0] }}</strong> and the <strong>{{ calculationData.secondPosition }}th</strong> number is <strong>{{ theTwoNumbers()[1] }}</strong>.
              </p>
              <div class="median-numbers">
                <span class="wrapper" v-html="`${colorMedianNumbers()}`" />
              </div>
            </div>
            <div v-else class="median-third top-margins median-odd">
              <p>
                Since the sample size is <strong>odd</strong> and has <strong>{{ getLengthOfArray }}</strong> observations, the median occupies the (n + 1)/2 = {{ inputSizePlusOne }}/2 =<strong>{{ positionWhenOdd }}th</strong> position and is equal to <strong>{{ medianWhenOdd }}</strong>.
              </p>
              <div class="median-numbers">
                <span class="wrapper" v-html="`${colorMedianNumbers()}`" />
              </div>
            </div>
          </li>
          <li>
            <p v-if="evenOrOdd">
              Calculate the mean of <span class="bolded-texts">{{ theTwoNumbers()[0] }}</span> and <span class="bolded-texts">{{ theTwoNumbers()[1] }}</span>.
            </p>
            <p v-if="evenOrOdd" class="formulas">
              \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ theTwoNumbers()[0] }}+{{ theTwoNumbers()[1] }} }{2}={{ calculationData.median }}\]
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p class="roman-number top-margins">
          Calculate the Mode.
        </p>
        <ol>
          <li>
            <p> Place all numbers into ascending or descending order.</p>
            <p class="formulas mode-array">
              \[{{ sortedStringifiedArray() }}\]
            </p>
          </li>
          <li>
            <p class="top-margins">
              Count how many times each value appears in the set.
            </p>
            <div
              class="table-outer-wrapper gradient bottom-margins top-margins"
              :class="{ 'no-gradient-after': endScrolledAfter, 'no-gradient-before': endScrolledBefore }"
            >
              <div
                class="col-12 table-wrapper"
                @scroll="handleScroll"
              >
                <div class="border-radius-wrapper">
                  <table>
                    <tr v-html="frequencyTableValuesRow" />
                    <tr v-html="frequencyTableFrequencyRow" />
                  </table>
                </div>
              </div>
            </div>
          </li>
          <li>
            <p v-if="calculationData.mode !== 'No Mode'" class="top-margins">
              Check the value that appears most frequently; The mode{{ plural }} in this dataset are:
            </p>
            <p v-else class="top-margins">
              Check the value that appears most frequently; No value appears more than once. Therefore, the dataset has:
            </p>
            <div class="mode-result">
              <p>
                {{ calculationData.mode }}
              </p>
            </div>
          </li>
        </ol>
      </li>
    </ol>

    <div class="python-r-codes python">
      <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
      <p class="codes-info">
        The following Python code should provide the mean, median, and mode using the input data you entered.
      </p>
      <div class="codes-container">
        <div class="codes">
          <pre
            class="language-python"
          ><code id="python"># Import relevant libraries

import numpy as np

from collections import Counter

# Data to be analyzed

data = <span class="no-wrap">{{ inputToNumbers }}</span>

# Calculate mean with numpy

mean = np.mean(data)

print("Mean:", mean)

# Calculate median with numpy

median = np.median(data)

print("Median:", median)

# Extract the mode(s)

counter = Counter(data) # Get the count of each element

# If every number is unique (it can be found only once in the data), there is no mode

if max(list(counter.values())) == 1:

    mode = "No mode"

# Otherwise, the mode corresponds to the number(s) with the most duplicates

else:

    mode = [k for k, v in counter.items() if v == max(list(counter.values()))]

print("Mode(s):", mode)</code></pre>
        </div>
      </div>
      <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python')">
        {{ copyCodeOrCopiedPython }}
      </button>
    </div>
    <div class="python-r-codes">
      <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
      <p class="codes-info">
        The following R code should provide the mean, median, and mode using the input data you entered.
      </p>
      <div class="codes-container">
        <div class="codes">
          <pre
            class="language-r"
          ><code id="r">data &lt;- c(<span class="no-wrap">{{ arrayToString }}</span>)

# mean

mean &lt;- mean(data)

# median

median &lt;- median(data)

# mode

mode_table &lt;- table(data)

mode_values &lt;- names(mode_table)[mode_table == max(mode_table)]

mode &lt;- as.numeric(mode_values)

cat("Mean:", mean, "\n")

cat("Median:", median, "\n")

if (length(mode_table) == length(data)) {

  cat("There is no mode.\n")

} else if (length(mode) == 1) {

  cat("Mode:", mode, "\n")

} else {

  cat("Modes:", paste(mode, collapse=", "), "\n")

} </code></pre>
        </div>
      </div>
      <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r')">
        {{ copyCodeOrCopiedR }}
      </button>
    </div>
  </div>
</template>

<script>
import calculatorsStepsMixin from '@itbpbg/lms-components/src/mixins/calculators/steps';

export default {
  mixins: [calculatorsStepsMixin],
  data() {
    return {
      frequencyTableValuesRow: ['<td class="headings">Values</td>'],
      frequencyTableFrequencyRow: ['<td class="headings">Frequency</td>'],
      endScrolledAfter: false,
      endScrolledBefore: true,
    };
  },
  computed: {
    additionString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `${num} + ` : num));
      return rawDataMeanArray.join('');
    },
    getLengthOfArray() {
      return this.calculationData.rawDataMeanArray.length;
    },
    arrayToString() {
      return this.calculationData.rawDataMeanArray.join(', ');
    },
    evenOrOdd() {
      return this.getLengthOfArray % 2 === 0;
    },
    inputSizePlusOne() {
      return this.getLengthOfArray + 1;
    },
    positionWhenOdd() {
      return this.inputSizePlusOne / 2;
    },
    medianWhenOdd() {
      return this.calculationData.rawDataMeanArray.slice().sort((a, b) => a - b)[this.positionWhenOdd - 1];
    },
    plural() {
      return Number.isNaN(Number(this.calculationData.mode)) ? 's' : '';
    },
  },
  mounted() {
    this.frequencyTableCreation();
  },
  methods: {
    handleScroll(event) {
      const { scrollLeft, scrollWidth, offsetWidth } = event.target;
      const contentWidth = scrollWidth - offsetWidth;
      if (contentWidth <= scrollLeft + 10) {
        this.endScrolledAfter = true;
      } else {
        this.endScrolledAfter = false;
      }

      if (scrollLeft - 10 >= 0) {
        this.endScrolledBefore = false;
      } else {
        this.endScrolledBefore = true;
      }
    },
    frequencyTableCreation() {
      const currentObject = this.calculationData.countsObject;
      let currentArray = Object.entries(currentObject).sort((a, b) => b[1] - a[1]);
      let currentModeArray = [];

      if (Number.isNaN(Number(Math.trunc(this.calculationData.mode)))) {
        currentModeArray = this.calculationData.mode.split(',');
      } else {
        currentModeArray.push(Math.trunc(this.calculationData.mode).toString());
      }

      const decimalTest = /^\d+\.{1}?\d+$/;
      currentArray = currentArray.map((arr) => (decimalTest.test(arr[0]) ? [Number(arr[0]).toFixed(this.calculationData.selectedDecimalPlaces), arr[1]] : arr));

      for (let i = 0; i < currentArray.length; i += 1) {
        if (currentModeArray.includes(currentArray[i][0])) {
          this.frequencyTableValuesRow.push(`<td class="bolder">${currentArray[i][0]}</td>`);
          this.frequencyTableFrequencyRow.push(`<td class="bolder">${currentArray[i][1]}</td>`);
        } else {
          this.frequencyTableValuesRow.push(`<td>${currentArray[i][0]}</td>`);
          this.frequencyTableFrequencyRow.push(`<td>${currentArray[i][1]}</td>`);
        }
      }

      this.frequencyTableValuesRow = this.frequencyTableValuesRow.join('');
      this.frequencyTableFrequencyRow = this.frequencyTableFrequencyRow.join('');
    },
    sortedStringifiedArray() {
      return this.calculationData.rawDataMeanArray.slice().sort((a, b) => a - b).join(',');
    },
    sortedArray() {
      return this.calculationData.rawDataMeanArray.slice().sort((a, b) => a - b);
    },
    theTwoNumbers() {
      return [this.sortedArray()[this.calculationData.firstPosition - 1], this.sortedArray()[this.calculationData.secondPosition - 1]];
    },
    colorMedianNumbers() {
      let resultArr;

      if (this.getLengthOfArray % 2 === 0) {
        resultArr = this.sortedArray().map((el, i) => (i === this.calculationData.firstPosition - 1
        || i === this.calculationData.secondPosition - 1
          ? `<span class="add-color">${el}</span>`
          : `<span>${el}</span>`));
      } else {
        resultArr = this.sortedArray().map((el, i) => (i === this.positionWhenOdd - 1
          ? `<span class="add-color">${el}</span>`
          : `<span>${el}</span>`));
      }

      return resultArr.join(',');
    },
  },
};
</script>

<style lang="scss" scoped>
.calculation-steps {
  .table-outer-wrapper {
    &.gradient {
      position: relative;

      &:after {
        content: "";
        width: 55px;
        height: 80px;
        position: absolute;
        right: 0;
        top: 0;
        background: linear-gradient(270deg, var(--main-light) 1.64%, rgba(255, 255, 255, 0) 84.62%);
      }

      &:before {
        content: "";
        width: 55px;
        height: 80px;
        position: absolute;
        left: 100px;
        top: 0;
        background: linear-gradient(90deg, var(--main-light) 1.64%, rgba(255, 255, 255, 0) 84.62%);
      }
    }

    &.no-gradient-after {
      &:after {
        content: none;
      }
    }

    &.no-gradient-before {
      &:before {
        content: none;
      }
    }

    .table-wrapper {
      min-height: 80px;
      overflow-y: hidden;
      overflow-x: scroll;

      &::-webkit-scrollbar {
        width: auto;
        height: 8px;
      }

      &::-webkit-scrollbar-track {
        background-color: var(--main-light);
      }

      &::-webkit-scrollbar-thumb {
        background: var(--main-light-shade-4);
        border-radius: 10px;
      }

      .border-radius-wrapper {
        display: table;
        margin-bottom: 10px;
        border-radius: 5px;

        ::v-deep table {
          border-collapse: separate;
          border-spacing: 0;

          td {
            height: 40px;
            border-right: 2px solid var(--accent-shade-2);
            border-bottom: 2px solid var(--accent-shade-2);
            text-align: center;
            min-width: 50px;
            border-color: var(--accent-shade-2);

            &:first-child {
              border-left: 2px solid var(--accent-shade-2);
            }
          }

          tr:first-child {
            td {
              border-top: 2px solid var(--accent-shade-2);
            }
          }

          tr:first-child td:first-child { border-top-left-radius: 5px; }
          tr:first-child td:last-child { border-top-right-radius: 5px; }
          tr:last-child td:first-child { border-bottom-left-radius: 5px; }
          tr:last-child td:last-child { border-bottom-right-radius: 5px; }

          .headings {
            min-width: 100px;
            color: var(--accent-shade-3);
            font-weight: 500;
            background: var(--accent-light-3);
            border-color: var(--accent-shade-2);
            position: sticky;
            left: 0;
          }

          .bolder {
            font-weight: 700;
          }
        }
      }
    }
  }

  .mode-array {
    ::v-deep .MathJax_SVG_Display {
      svg {
        font-size: 15.5px;
      }
    }
  }

  .mode-result {
    font-weight: 600;
    font-size: 20px;
    line-height: 110%;
    text-align: center;
    color: var(--accent-shade-2);
    margin-top: var(--spacing-16);
    overflow: auto;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--main-light-shade-3);
      border-radius: 10px;
    }

    p {
      margin-bottom: var(--spacing-12);
    }
  }

  .python-r-codes {
    &.python {
      margin-top: 52px;
    }
  }

  .median-third {
    display: flex;
    align-items: center;
    margin-bottom: var(--spacing-16);

    &.median-odd {
      margin-bottom: 0;
    }

    .median-numbers {
      width: 100%;
      overflow: auto;

      &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }

      &::-webkit-scrollbar-thumb {
        background: var(--main-light-shade-3);
        border-radius: 10px;
      }

      .wrapper {
        display: flex;
        align-items: center;
        margin: 0 auto;

        ::v-deep span {
          font-size: 20px;
          font-family: math, sans-serif;
          margin: var(--spacing-16) 0;

          &.add-color {
            font-weight: 600;
            color: var(--accent-shade-3);
          }
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .calculation-steps {
    .table-outer-wrapper {
      margin: 10px 0 var(--spacing-28);

      &.gradient:before {
        left: 97px;
      }

      .table-wrapper {
        .border-radius-wrapper {
          ::v-deep table {
            .headings {
              min-width: 97px;
            }
          }
        }
      }
    }
  }
}

@import '../../../../assets/sass/ds/calculators/calculation-steps.scss';
</style>
